import { CheckedIcon } from '@components/icons/Checked'
import { Dispatch, SetStateAction, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useButtonClick } from '@/hooks/useButtonClick'
import { useChoices } from '@/hooks/useChoices'
import i18n from '@/locale/i18n'
import { BUTTON_ACTION_TYPE, RadioOptionComponent } from '@/types/components'

type RadioOptionProps = Omit<RadioOptionComponent, 'componentType'> & {
  selected: string | null
  setSelected: Dispatch<SetStateAction<string | null>>
  tabIndex: number
  initialSelected: string | null
  groupError?: string
}

export const RadioOption = ({ id, textKey, value, buttonAction, selected, setSelected, tabIndex, initialSelected, groupError }: RadioOptionProps) => {
  const { t } = useTranslation()
  const { handleButtonClick } = useButtonClick()
  const { choices } = useChoices()

  const handleChange = async () => {
    if (buttonAction?.type === BUTTON_ACTION_TYPE.SET_LANGUAGE) {
      localStorage.setItem('languageSelected', value)
      await i18n.changeLanguage(value)
      return window.location.reload()
    }

    setSelected(id)
  }

  useEffect(() => {
    if (choices?.length < 1 || !buttonAction || selected !== id) return

    handleButtonClick(buttonAction)
  }, [choices])

  const border = groupError ? 'border-error' : 'border-gray'

  return (
    <span className="relative" data-testid={`${id}-container`}>
      <input
        id={id}
        tabIndex={tabIndex}
        data-testid={`${id}-input`}
        type="checkbox" // This needs to be checkbox in order to be able to use the tabindex navigation with tab key
        value={value}
        className="peer absolute h-0 w-0 opacity-0"
        checked={selected === id || (!selected && initialSelected === id)}
        onChange={handleChange}
      />

      <label
        htmlFor={id}
        className={`flex w-full cursor-pointer justify-center rounded-lg border bg-gray-background py-5 px-8 text-gray-text peer-checked:border-2 peer-checked:border-primary peer-checked:bg-primary-light peer-checked:font-bold peer-checked:text-primary peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-primary ${border}`}
      >
        {t(textKey)}
      </label>

      <CheckedIcon customClassName="absolute top-1/2 right-4 hidden -translate-y-1/2  transform peer-checked:block fill-primary" />
    </span>
  )
}
